import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ImagePlus, Trash2 } from 'lucide-react';
import settingsService from '../../services/settings.service';
import { getError } from '../../utils';
import { Button } from '../ui/button';

/** Placeholders replaced server-side (backend/utils/cartaPdf.js). */
export const CARTA_PLACEHOLDERS = ['nombre', 'plan', 'bono', 'fecha', 'año', 'monto', 'numeros'];

const TEXT_FIELDS = [
  { name: 'encabezado', rows: 3 },
  { name: 'saludo', rows: 1 },
  { name: 'cuerpo', rows: 12 },
  { name: 'firma', rows: 2 },
];

const fieldCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring';

/**
 * Letter ("Carta") PDF form: texts with placeholder chips (inserted at the caret of the last
 * focused field), company logo toggle, optional side image and accent color.
 * `carta` = { encabezado, saludo, cuerpo, firma, mostrarLogo, imagenLateral, colorAcento }.
 */
export default function CartaEditor({ carta, onChange, imagenLateralUrl, onImagenLateralUrl }) {
  const { t } = useTranslation();
  const refs = useRef({});
  const [focused, setFocused] = useState('cuerpo');
  const [uploading, setUploading] = useState(false);

  const set = (name, value) => onChange({ ...carta, [name]: value });

  const insertPlaceholder = key => {
    const name = focused || 'cuerpo';
    const el = refs.current[name];
    const token = `{${key}}`;
    const value = carta[name] || '';
    const start = el ? el.selectionStart : value.length;
    const end = el ? el.selectionEnd : value.length;
    set(name, value.slice(0, start) + token + value.slice(end));
    window.requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const caret = start + token.length;
      el.setSelectionRange(caret, caret);
    });
  };

  const uploadImage = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      const r = Math.random().toString(36).substring(7);
      formData.append('file', file, file.name.replaceAll(' ', '') + r);
      formData.append('type', 'cartaImagenLateral');
      const doc = await settingsService.uploadSettingsImg(formData);
      onChange({ ...carta, imagenLateral: doc._id });
      onImagenLateralUrl(doc.fileLink || '');
      toast.success(t('common.imageAdded'));
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className='space-y-4'>
      <div className='space-y-1.5'>
        <p className='text-xs font-medium text-foreground'>
          {t('ticketDesign.carta.placeholders')}
        </p>
        <div className='flex flex-wrap gap-1.5'>
          {CARTA_PLACEHOLDERS.map(key => (
            <button
              key={key}
              type='button'
              onMouseDown={e => e.preventDefault()}
              onClick={() => insertPlaceholder(key)}
              title={t(`ticketDesign.carta.ph.${key}`)}
              className='rounded-full border border-border bg-muted px-2.5 py-0.5 font-mono text-xs text-foreground hover:bg-accent'
            >
              {`{${key}}`}
            </button>
          ))}
        </div>
        <p className='text-xs text-muted-foreground'>{t('ticketDesign.carta.placeholdersHint')}</p>
      </div>

      {TEXT_FIELDS.map(({ name, rows }) => (
        <label key={name} className='block space-y-1 text-xs text-muted-foreground'>
          <span className='font-medium text-foreground'>{t(`ticketDesign.carta.${name}`)}</span>
          {rows === 1 ? (
            <input
              ref={el => {
                refs.current[name] = el;
              }}
              className={`${fieldCls} h-9`}
              value={carta[name] || ''}
              onFocus={() => setFocused(name)}
              onChange={e => set(name, e.target.value)}
            />
          ) : (
            <textarea
              ref={el => {
                refs.current[name] = el;
              }}
              rows={rows}
              className={fieldCls}
              value={carta[name] || ''}
              onFocus={() => setFocused(name)}
              onChange={e => set(name, e.target.value)}
            />
          )}
          {name === 'cuerpo' && <span className='block'>{t('ticketDesign.carta.cuerpoHint')}</span>}
        </label>
      ))}

      <label className='flex items-center gap-2 text-sm text-foreground'>
        <input
          type='checkbox'
          className='h-4 w-4 rounded border-input'
          checked={carta.mostrarLogo !== false}
          onChange={e => set('mostrarLogo', e.target.checked)}
        />
        {t('ticketDesign.carta.mostrarLogo')}
      </label>

      <div className='space-y-1.5'>
        <p className='text-xs font-medium text-foreground'>{t('ticketDesign.carta.colorAcento')}</p>
        <div className='flex items-center gap-2'>
          <input
            type='color'
            className='h-9 w-12 cursor-pointer rounded border border-input bg-background'
            value={carta.colorAcento || '#1f2937'}
            onChange={e => set('colorAcento', e.target.value)}
          />
          <span className='font-mono text-xs text-muted-foreground'>
            {carta.colorAcento || t('ticketDesign.carta.colorDefault')}
          </span>
          {carta.colorAcento && (
            <Button type='button' variant='ghost' size='sm' onClick={() => set('colorAcento', '')}>
              {t('ticketDesign.carta.colorReset')}
            </Button>
          )}
        </div>
      </div>

      <div className='space-y-1.5'>
        <p className='text-xs font-medium text-foreground'>
          {t('ticketDesign.carta.imagenLateral')}
        </p>
        <p className='text-xs text-muted-foreground'>{t('ticketDesign.carta.imagenLateralHint')}</p>
        <div className='flex flex-wrap items-center gap-2'>
          {carta.imagenLateral && imagenLateralUrl && (
            <img
              src={imagenLateralUrl}
              alt=''
              className='h-16 w-8 rounded border border-border object-cover'
            />
          )}
          <label className='inline-flex cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-medium shadow-sm hover:bg-accent'>
            <ImagePlus className='h-4 w-4' />
            {uploading ? t('ticketDesign.carta.uploading') : t('ticketDesign.carta.uploadImage')}
            <input
              type='file'
              accept='image/*'
              className='hidden'
              disabled={uploading}
              onChange={uploadImage}
            />
          </label>
          {carta.imagenLateral && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={() => {
                onChange({ ...carta, imagenLateral: null });
                onImagenLateralUrl('');
              }}
            >
              <Trash2 className='h-4 w-4' />
              {t('ticketDesign.carta.removeImage')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
